import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { ImageOff, ImagePlus, Lock } from "lucide-react";
import { absoluteUrl } from "../api/client";
import {
  boxFromPoints,
  canRotate,
  containsPoint,
  imageFilter,
  imagePlacement,
  itemTransform,
  normaliseAngle,
  pointsOnBoard,
  radiusCqw,
  resizeTurned,
  selectionBounds,
  shadowFilter,
  visualBox,
} from "./geometry";
import { aspectRatio, makeArrow, makeDrawing, makeShape } from "./templates";
import { DEFAULT_BEND, HIGHLIGHTER_ALPHA, bendFromPoint, curveMidpoint, dashArray, vectorParts } from "./vector";

export const ASSET_MIME = "application/x-jumig-asset";

const DRAG_THRESHOLD = 3;
const SNAP = 0.8;
const MIN_SIZE = 3;
const HANDLES = ["nw", "ne", "sw", "se"];
// Must match the renderer: caption and swatch-label sizes in cqw.
const CAPTION_PAD = 0.6;
const ROUND_SWATCH = 25;
export const VECTOR_TYPES = ["draw", "arrow", "shape"];
// Box and arrow drags shorter than this (board-width %) count as a stray click.
const MIN_DRAG = 1.2;
// Shift turns in these steps; otherwise the angle settles on a right angle
// when it comes this close.
const ROTATE_STEP = 15;
const ROTATE_SNAP = 3;

// Same order as FONT_FILES in api/moodboard_render.py.
export const FONT_STACKS = {
  sans: 'Arial, "Liberation Sans", "Helvetica Neue", Helvetica, "DejaVu Sans", sans-serif',
  serif: 'Georgia, "DejaVu Serif", "Liberation Serif", serif',
};

const clamp = (value, low, high) => Math.min(high, Math.max(low, value));
const round = (value) => Math.round(value * 1000) / 1000;

export const assetKey = (item) =>
  item.type === "image" && item.source && item.ref ? `${item.source}:${item.ref}` : null;

export function contrastText(hex) {
  const value = /^#[0-9a-f]{6}$/i.test(hex || "") ? hex : "#cccccc";
  const [r, g, b] = [1, 3, 5].map((i) => parseInt(value.slice(i, i + 2), 16));
  return 0.299 * r + 0.587 * g + 0.114 * b > 150 ? "#1e1b16" : "#ffffff";
}

function bestSnap(edges, lines, threshold) {
  let best = null;
  edges.forEach((edge) => {
    lines.forEach((line) => {
      const delta = line - edge;
      if (Math.abs(delta) <= threshold && (!best || Math.abs(delta) < Math.abs(best.delta))) {
        best = { delta, line };
      }
    });
  });
  return best;
}

function snapLines(items, ratio) {
  const others = items.map((item) => visualBox(item, ratio));
  return {
    xs: [0, 50, 100, ...others.flatMap((o) => [o.x, o.x + o.w / 2, o.x + o.w])],
    ys: [0, 50, 100, ...others.flatMap((o) => [o.y, o.y + o.h / 2, o.y + o.h])],
  };
}

// Snaps the moving group's bounds; returns the extra offset and guide lines.
function snapMove(bounds, others, ratio) {
  const { xs, ys } = snapLines(others, ratio);
  const sx = bestSnap([bounds.x, bounds.x + bounds.w / 2, bounds.x + bounds.w], xs, SNAP);
  const sy = bestSnap([bounds.y, bounds.y + bounds.h / 2, bounds.y + bounds.h], ys, SNAP * ratio);
  return {
    dx: sx ? sx.delta : 0,
    dy: sy ? sy.delta : 0,
    guides: { x: sx ? sx.line : null, y: sy ? sy.line : null },
  };
}

function snapResize(box, handle, others, ratio) {
  const { xs, ys } = snapLines(others, ratio);
  const next = { ...box };
  const guides = { x: null, y: null };
  const right = box.x + box.w;
  const bottom = box.y + box.h;
  if (handle.includes("w")) {
    const s = bestSnap([box.x], xs, SNAP);
    if (s && right - s.line >= MIN_SIZE) {
      next.x = s.line;
      next.w = right - s.line;
      guides.x = s.line;
    }
  } else {
    const s = bestSnap([right], xs, SNAP);
    if (s && s.line - box.x >= MIN_SIZE) {
      next.w = s.line - box.x;
      guides.x = s.line;
    }
  }
  if (handle.includes("n")) {
    const s = bestSnap([box.y], ys, SNAP * ratio);
    if (s && bottom - s.line >= MIN_SIZE) {
      next.y = s.line;
      next.h = bottom - s.line;
      guides.y = s.line;
    }
  } else {
    const s = bestSnap([bottom], ys, SNAP * ratio);
    if (s && s.line - box.y >= MIN_SIZE) {
      next.h = s.line - box.y;
      guides.y = s.line;
    }
  }
  return { box: next, guides };
}

function resizeBox(origin, handle, dx, dy) {
  let { x, y, w, h } = origin;
  const right = x + w;
  const bottom = y + h;
  if (handle.includes("w")) {
    x = clamp(origin.x + dx, 0, right - MIN_SIZE);
    w = right - x;
  } else {
    w = clamp(origin.w + dx, MIN_SIZE, 100 - x);
  }
  if (handle.includes("n")) {
    y = clamp(origin.y + dy, 0, bottom - MIN_SIZE);
    h = bottom - y;
  } else {
    h = clamp(origin.h + dy, MIN_SIZE, 100 - y);
  }
  return { x, y, w, h };
}

function ImageBody({ item, asset, ratio, showPlaceholders }) {
  const [natural, setNatural] = useState(null);
  if (!asset?.url) {
    if (!showPlaceholders) return null;
    return (
      <div className="mb-slot">
        {item.source ? (
          <>
            <ImageOff size={18} strokeWidth={1.6} aria-hidden="true" />
            <span>Image unavailable</span>
          </>
        ) : (
          <>
            <ImagePlus size={18} strokeWidth={1.6} aria-hidden="true" />
            <span>Drop an image</span>
          </>
        )}
      </div>
    );
  }

  const width = asset.width || natural?.width;
  const height = asset.height || natural?.height;
  const geometry = width && height ? imagePlacement(item, width, height, ratio) : null;
  const flipped = item.flip_x || item.flip_y;
  return (
    <img
      src={absoluteUrl(asset.url)}
      alt={asset.name || ""}
      draggable={false}
      onLoad={(event) =>
        !asset.width && setNatural({ width: event.currentTarget.naturalWidth, height: event.currentTarget.naturalHeight })
      }
      style={{
        ...(geometry
          ? {
              position: "absolute",
              width: `${geometry.width}%`,
              height: `${geometry.height}%`,
              left: `${geometry.left}%`,
              top: `${geometry.top}%`,
            }
          : {
              width: "100%",
              height: "100%",
              objectFit: item.fit === "contain" ? "contain" : "cover",
              // Until its size is known a cropped image stays hidden rather than jump.
              visibility: item.crop ? "hidden" : undefined,
            }),
        transform: flipped ? `scale(${item.flip_x ? -1 : 1}, ${item.flip_y ? -1 : 1})` : undefined,
        // A whole-image frame has room around the crop, so hide what was cropped away.
        clipPath: item.crop
          ? `inset(${item.crop.y * 100}% ${(1 - item.crop.x - item.crop.w) * 100}% ${(1 - item.crop.y - item.crop.h) * 100}% ${item.crop.x * 100}%)`
          : undefined,
        filter: imageFilter(item),
      }}
    />
  );
}

// Drawn in board-width units (the same as cqw), so stroke widths match the
// export exactly; see _render_vector() in api/moodboard_render.py.
const pointList = (points) => points.map((point) => point.join(",")).join(" ");

export function VectorBody({ item, ratio }) {
  const width = item.w;
  const height = item.h / ratio;
  const sw = item.stroke_width || 0;
  const highlighter = item.type === "draw" && item.brush === "highlighter";

  return (
    <svg className="mb-vector" viewBox={`0 0 ${width} ${height}`} preserveAspectRatio="none" aria-hidden="true">
      {/* Group opacity keeps a highlighter even where the stroke crosses itself. */}
      <g opacity={highlighter ? HIGHLIGHTER_ALPHA : undefined}>
        {vectorParts(item, ratio).map((part, index) => {
          if (part.kind === "fill") {
            return part.points.length >= 3 ? <polygon key={index} points={pointList(part.points)} fill={part.color || item.stroke} /> : null;
          }
          if (part.kind === "circle") {
            return <circle key={index} cx={part.center[0]} cy={part.center[1]} r={part.r} fill={item.stroke} />;
          }
          if (part.points.length < 2 || sw <= 0) return null;
          const Line = part.closed ? "polygon" : "polyline";
          return (
            <Line
              key={index}
              points={pointList(part.points)}
              fill="none"
              stroke={item.stroke}
              strokeWidth={sw}
              strokeLinecap={part.roundCaps ? "round" : "butt"}
              strokeLinejoin="round"
              strokeDasharray={dashArray(part.dash, sw)}
            />
          );
        })}
      </g>
    </svg>
  );
}

// Live preview while drawing: the item that will be created, drawn the same way.
function DraftPreview({ item, ratio }) {
  return (
    <div
      className={`mb-item mb-item-${item.type} mb-draft-item`}
      style={{ left: `${item.x}%`, top: `${item.y}%`, width: `${item.w}%`, height: `${item.h}%` }}
    >
      <VectorBody item={item} ratio={ratio} />
    </div>
  );
}

function ItemBody({ item, asset, ratio, showPlaceholders }) {
  if (VECTOR_TYPES.includes(item.type)) return <VectorBody item={item} ratio={ratio} />;

  const radius = radiusCqw(item, ratio);

  if (item.type === "image") {
    return (
      <>
        <ImageBody key={asset?.url || "empty"} item={item} asset={asset} ratio={ratio} showPlaceholders={showPlaceholders} />
        {item.caption && asset?.name && (
          <div
            className="mb-caption"
            style={{ padding: `${CAPTION_PAD}cqw ${Math.max(CAPTION_PAD, radius * 0.6)}cqw` }}
          >
            {asset.name}
          </div>
        )}
        {item.border_width > 0 && (
          <div
            className="mb-border"
            style={{ border: `${item.border_width}cqw solid ${item.border_color}`, borderRadius: `${radius}cqw` }}
          />
        )}
      </>
    );
  }

  if (item.type === "text") {
    return (
      <div
        className="mb-text"
        style={{
          fontSize: `${item.font_size}cqw`,
          fontFamily: FONT_STACKS[item.font_family] || FONT_STACKS.sans,
          fontWeight: item.font_weight === "bold" ? 700 : 400,
          color: item.color,
          textAlign: item.align,
        }}
      >
        {item.text}
      </div>
    );
  }

  const centred = (item.radius || 0) >= ROUND_SWATCH;
  return (
    <div className="mb-swatch" style={{ background: item.color }}>
      {item.label && (
        <span className={centred ? "is-centred" : ""} style={{ color: contrastText(item.color) }}>
          {item.label}
        </span>
      )}
    </div>
  );
}

export default function BoardCanvas({
  board,
  assets = {},
  editable = false,
  fit = false,
  compact = false,
  showPlaceholders = editable,
  selectedIds = [],
  onSelect,
  onChange,
  onDropAsset,
  onDropFiles,
  tool = "select",
  toolStyle = {},
  onCreate,
  onOpenCrop,
}) {
  const ratio = aspectRatio(board.aspect);
  const stageRef = useRef(null);
  const boardRef = useRef(null);
  const gesture = useRef(null);
  const [fitWidth, setFitWidth] = useState(null);
  const [guides, setGuides] = useState(null);
  const [marquee, setMarquee] = useState(null);
  const [dropTarget, setDropTarget] = useState(null);
  const [draft, setDraft] = useState(null);
  const [turning, setTurning] = useState(null);

  // Switching tool (or pressing Esc) mid-stroke abandons that stroke.
  useEffect(() => setDraft(null), [tool]);

  useLayoutEffect(() => {
    if (!fit) return undefined;
    const node = stageRef.current;
    const measure = () => {
      const { width, height } = node.getBoundingClientRect();
      setFitWidth(Math.max(0, Math.floor(Math.min(width, height * ratio))));
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(node);
    return () => observer.disconnect();
  }, [fit, ratio]);

  const items = board.items || [];
  const ordered = [...items].sort((a, b) => a.z - b.z);
  const selected = items.filter((item) => selectedIds.includes(item.id));
  const single = selected.length === 1 ? selected[0] : null;

  const clampPoint = (point) => ({ x: clamp(point.x, 0, 100), y: clamp(point.y, 0, 100) });

  const pointFromEvent = (event) => {
    const rect = boardRef.current.getBoundingClientRect();
    return {
      x: ((event.clientX - rect.left) / rect.width) * 100,
      y: ((event.clientY - rect.top) / rect.height) * 100,
    };
  };

  const startItemGesture = (event, item, handle = null) => {
    if (!editable || event.button !== 0) return;
    event.stopPropagation();

    const additive = event.shiftKey || event.ctrlKey || event.metaKey;
    if (additive && !handle) {
      onSelect?.(
        selectedIds.includes(item.id) ? selectedIds.filter((id) => id !== item.id) : [...selectedIds, item.id]
      );
      return;
    }

    const alreadySelected = selectedIds.includes(item.id);
    const ids = alreadySelected && !handle ? selectedIds : [item.id];
    if (!alreadySelected || handle) onSelect?.(ids);

    const moving = items.filter((entry) => ids.includes(entry.id) && !entry.locked);
    if (!moving.length) return;

    event.currentTarget.setPointerCapture(event.pointerId);
    gesture.current = {
      kind: handle ? "resize" : "move",
      id: item.id,
      handle,
      startX: event.clientX,
      startY: event.clientY,
      rect: boardRef.current.getBoundingClientRect(),
      origins: new Map(
        moving.map((entry) => [
          entry.id,
          {
            x: entry.x,
            y: entry.y,
            w: entry.w,
            h: entry.h,
            type: entry.type,
            rotation: entry.rotation,
            tilt_x: entry.tilt_x,
            tilt_y: entry.tilt_y,
          },
        ])
      ),
      before: items,
      active: false,
      // A plain click inside a multi-selection narrows it to that one item.
      collapseTo: alreadySelected && selectedIds.length > 1 ? item.id : null,
    };
  };

  const startEndpoint = (event, item, index) => {
    if (!editable || event.button !== 0) return;
    event.stopPropagation();
    event.currentTarget.setPointerCapture(event.pointerId);
    gesture.current = {
      kind: "endpoint",
      id: item.id,
      index,
      fixed: pointsOnBoard(item)[1 - index],
      startX: event.clientX,
      startY: event.clientY,
      before: items,
      active: false,
    };
  };

  // Shift keeps arrows at 45-degree steps and boxes square.
  const constrain = (start, point, kind, shift) => {
    if (!shift) return point;
    const dx = point.x - start.x;
    const dy = (point.y - start.y) / ratio;
    if (kind === "arrow") {
      const angle = Math.round(Math.atan2(dy, dx) / (Math.PI / 4)) * (Math.PI / 4);
      const length = Math.hypot(dx, dy);
      return clampPoint({ x: start.x + Math.cos(angle) * length, y: start.y + Math.sin(angle) * length * ratio });
    }
    const side = Math.max(Math.abs(dx), Math.abs(dy));
    return clampPoint({ x: start.x + Math.sign(dx || 1) * side, y: start.y + Math.sign(dy || 1) * side * ratio });
  };

  // The item a drag would create, used both for the live preview and the result.
  const itemFromDraft = (points, final) => {
    if (!points || points.length < 2) return null;
    const [ax, ay] = points[0];
    const [bx, by] = points[points.length - 1];
    if (tool === "pencil") return makeDrawing(boxFromPoints(points, ratio), toolStyle);
    if (tool === "arrow") {
      if (final && Math.hypot(bx - ax, (by - ay) / ratio) < MIN_DRAG) return null;
      return makeArrow(boxFromPoints([[ax, ay], [bx, by]], ratio), toolStyle);
    }
    const w = Math.abs(bx - ax);
    const h = Math.abs(by - ay);
    if (final && w < MIN_DRAG && h / ratio < MIN_DRAG) return null;
    return makeShape({ x: Math.min(ax, bx), y: Math.min(ay, by), w: Math.max(w, 1), h: Math.max(h, 1) }, toolStyle);
  };

  const boardMeasured = () => {
    const rect = boardRef.current?.getBoundingClientRect();
    return Boolean(rect && rect.width > 0 && rect.height > 0);
  };

  const startDraw = (event) => {
    // A board that has not been laid out yet has no size to map the pointer onto.
    if (event.button !== 0 || !boardMeasured()) return;
    event.stopPropagation();
    event.currentTarget.setPointerCapture(event.pointerId);
    const point = clampPoint(pointFromEvent(event));
    setDraft({ points: [[point.x, point.y]] });
  };

  const moveDraw = (event) => {
    if (!draft) return;
    const point = clampPoint(pointFromEvent(event));
    if (tool === "pencil") {
      setDraft((current) => (current ? { points: [...current.points, [point.x, point.y]] } : current));
    } else {
      const [sx, sy] = draft.points[0];
      const end = constrain({ x: sx, y: sy }, point, tool, event.shiftKey);
      setDraft((current) => (current ? { points: [current.points[0], [end.x, end.y]] } : current));
    }
  };

  const endDraw = () => {
    const item = itemFromDraft(draft?.points, true);
    setDraft(null);
    if (item) onCreate?.(item, { keepTool: tool === "pencil" });
  };

  const startRotate = (event, item) => {
    if (!editable || event.button !== 0) return;
    event.stopPropagation();
    event.currentTarget.setPointerCapture(event.pointerId);
    const rect = boardRef.current.getBoundingClientRect();
    gesture.current = {
      kind: "rotate",
      id: item.id,
      centre: {
        x: rect.left + ((item.x + item.w / 2) / 100) * rect.width,
        y: rect.top + ((item.y + item.h / 2) / 100) * rect.height,
      },
      startX: event.clientX,
      startY: event.clientY,
      before: items,
      active: false,
    };
  };

  const startBend = (event, item) => {
    if (!editable || event.button !== 0) return;
    event.stopPropagation();
    event.currentTarget.setPointerCapture(event.pointerId);
    gesture.current = {
      kind: "bend",
      id: item.id,
      ends: pointsOnBoard(item),
      startX: event.clientX,
      startY: event.clientY,
      before: items,
      active: false,
    };
  };

  const startMarquee = (event) => {
    if (!editable || event.button !== 0 || !boardMeasured()) return;
    const additive = event.shiftKey || event.ctrlKey || event.metaKey;
    if (!additive) onSelect?.([]);
    event.currentTarget.setPointerCapture(event.pointerId);
    const point = pointFromEvent(event);
    gesture.current = {
      kind: "marquee",
      start: point,
      startX: event.clientX,
      startY: event.clientY,
      base: additive ? selectedIds : [],
      active: false,
    };
  };

  const handlePointerMove = (event) => {
    const g = gesture.current;
    if (!g) return;
    if (!g.active && Math.hypot(event.clientX - g.startX, event.clientY - g.startY) < DRAG_THRESHOLD) return;
    g.active = true;

    if (g.kind === "rotate") {
      const raw = (Math.atan2(event.clientY - g.centre.y, event.clientX - g.centre.x) * 180) / Math.PI + 90;
      let angle = raw;
      if (event.shiftKey) {
        angle = Math.round(raw / ROTATE_STEP) * ROTATE_STEP;
      } else {
        const square = Math.round(raw / 90) * 90;
        if (Math.abs(raw - square) <= ROTATE_SNAP) angle = square;
      }
      angle = normaliseAngle(angle);
      setTurning(angle);
      onChange?.(
        items.map((item) => (item.id === g.id ? { ...item, rotation: angle } : item)),
        { record: false }
      );
      return;
    }

    if (g.kind === "bend") {
      const point = pointFromEvent(event);
      const toUnits = ([x, y]) => [x, y / ratio];
      const bend = bendFromPoint(toUnits(g.ends[0]), toUnits(g.ends[1]), [point.x, point.y / ratio]);
      onChange?.(
        items.map((item) => (item.id === g.id ? { ...item, bend: Math.round(bend * 1000) / 1000 } : item)),
        { record: false }
      );
      return;
    }

    if (g.kind === "endpoint") {
      const point = clampPoint(pointFromEvent(event));
      const moved = constrain({ x: g.fixed[0], y: g.fixed[1] }, point, "arrow", event.shiftKey);
      const ends = g.index === 0 ? [[moved.x, moved.y], g.fixed] : [g.fixed, [moved.x, moved.y]];
      const shape = boxFromPoints(ends, ratio);
      onChange?.(
        items.map((item) => (item.id === g.id ? { ...item, ...shape } : item)),
        { record: false }
      );
      return;
    }

    if (g.kind === "marquee") {
      const point = pointFromEvent(event);
      const box = {
        x: Math.min(g.start.x, point.x),
        y: Math.min(g.start.y, point.y),
        w: Math.abs(point.x - g.start.x),
        h: Math.abs(point.y - g.start.y),
      };
      setMarquee(box);
      const hit = items
        .filter((item) => {
          const seen = visualBox(item, ratio);
          return seen.x < box.x + box.w && seen.x + seen.w > box.x && seen.y < box.y + box.h && seen.y + seen.h > box.y;
        })
        .map((item) => item.id);
      onSelect?.([...new Set([...g.base, ...hit])]);
      return;
    }

    const dx = ((event.clientX - g.startX) / g.rect.width) * 100;
    const dy = ((event.clientY - g.startY) / g.rect.height) * 100;
    const others = items.filter((item) => !g.origins.has(item.id));

    if (g.kind === "resize") {
      const origin = g.origins.get(g.id);
      const turned = canRotate(origin) && origin.rotation;
      let box;
      let lines = null;
      if (turned || event.shiftKey) {
        // Turned items resize along their own sides; Shift keeps the shape.
        box = resizeTurned(
          turned ? origin : { ...origin, rotation: 0 },
          g.handle,
          event.clientX - g.startX,
          event.clientY - g.startY,
          g.rect,
          { keepAspect: event.shiftKey, minSize: MIN_SIZE }
        );
      } else {
        const raw = resizeBox(origin, g.handle, dx, dy);
        ({ box, guides: lines } = event.altKey ? { box: raw, guides: null } : snapResize(raw, g.handle, others, ratio));
      }
      setGuides(lines);
      onChange?.(
        items.map((item) =>
          item.id === g.id ? { ...item, x: round(box.x), y: round(box.y), w: round(box.w), h: round(box.h) } : item
        ),
        { record: false }
      );
      return;
    }

    // Move the whole selection together, kept inside the board.
    const origins = [...g.origins.values()];
    const bounds = selectionBounds(origins);
    let mx = clamp(dx, -bounds.x, 100 - bounds.x - bounds.w);
    let my = clamp(dy, -bounds.y, 100 - bounds.y - bounds.h);
    let lines = null;
    if (!event.altKey) {
      // Snap what the eye sees: the outline of turned items, not their frame.
      const seen = selectionBounds(origins.map((origin) => visualBox(origin, ratio)));
      const snapped = snapMove({ ...seen, x: seen.x + mx, y: seen.y + my }, others, ratio);
      mx = clamp(mx + snapped.dx, -bounds.x, 100 - bounds.x - bounds.w);
      my = clamp(my + snapped.dy, -bounds.y, 100 - bounds.y - bounds.h);
      lines = snapped.guides;
    }
    setGuides(lines);
    onChange?.(
      items.map((item) => {
        const origin = g.origins.get(item.id);
        return origin ? { ...item, x: round(origin.x + mx), y: round(origin.y + my) } : item;
      }),
      { record: false }
    );
  };

  const endGesture = () => {
    const g = gesture.current;
    gesture.current = null;
    setGuides(null);
    setMarquee(null);
    setTurning(null);
    if (!g) return;
    if (g.kind !== "marquee" && g.active) onChange?.(items, { record: true, before: g.before });
    if (!g.active && g.collapseTo) onSelect?.([g.collapseTo]);
  };

  const imageAt = (point) =>
    [...ordered]
      .reverse()
      .find(
        (item) =>
          item.type === "image" && !item.locked && containsPoint(item, point, ratio)
      );

  const acceptsDrag = (event) =>
    editable &&
    (event.dataTransfer.types.includes(ASSET_MIME) || event.dataTransfer.types.includes("Files"));

  const handleDragOver = (event) => {
    if (!acceptsDrag(event)) return;
    event.preventDefault();
    event.dataTransfer.dropEffect = "copy";
    const target = imageAt(pointFromEvent(event))?.id || "board";
    if (target !== dropTarget) setDropTarget(target);
  };

  const handleDragLeave = (event) => {
    if (!boardRef.current?.contains(event.relatedTarget)) setDropTarget(null);
  };

  const handleDrop = (event) => {
    if (!acceptsDrag(event)) return;
    event.preventDefault();
    setDropTarget(null);
    const raw = pointFromEvent(event);
    const point = { x: clamp(raw.x, 0, 100), y: clamp(raw.y, 0, 100) };
    const targetId = imageAt(point)?.id || null;
    const payload = event.dataTransfer.getData(ASSET_MIME);
    if (payload) {
      try {
        onDropAsset?.(JSON.parse(payload), { targetId, point });
      } catch {
        // Not one of ours.
      }
    } else if (event.dataTransfer.files?.length) {
      onDropFiles?.(Array.from(event.dataTransfer.files), { targetId, point });
    }
  };

  const boardStyle = {
    background: board.background,
    aspectRatio: `${ratio}`,
    ...(fit && fitWidth != null ? { width: `${fitWidth}px` } : {}),
  };

  const boxStyle = (item) => ({
    left: `${item.x}%`,
    top: `${item.y}%`,
    width: `${item.w}%`,
    height: `${item.h}%`,
  });

  const turn = (item) => itemTransform(item, ratio);
  const groupBounds = selected.length > 1 ? selectionBounds(selected.map((item) => visualBox(item, ratio))) : null;
  // The turn handle sits above the item, or below it when that would be off the board.
  const handleBelow = (item) => {
    const radians = ((item.rotation || 0) * Math.PI) / 180;
    const reach = item.h / 2 + 6;
    return item.y + item.h / 2 - Math.cos(radians) * reach < 0;
  };

  const boardNode = (
    <div
      ref={boardRef}
      className={[
        "mb-board",
        editable ? "is-editable" : "",
        compact ? "is-compact" : "",
        dropTarget === "board" ? "is-drop-target" : "",
      ].join(" ")}
      style={boardStyle}
      onPointerDown={startMarquee}
      onPointerMove={handlePointerMove}
      onPointerUp={endGesture}
      onPointerCancel={endGesture}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      {ordered.map((item) => {
        const shadow = shadowFilter(item);
        const placed = { ...boxStyle(item), zIndex: item.z, opacity: item.opacity ?? 1 };
        const body = (
          <div
            key={item.id}
            className={[
              "mb-item",
              `mb-item-${item.type}`,
              item.locked ? "is-locked" : "",
              dropTarget === item.id ? "is-drop-target" : "",
            ].join(" ")}
            style={{
              ...(shadow ? { left: 0, top: 0, width: "100%", height: "100%" } : placed),
              borderRadius: VECTOR_TYPES.includes(item.type) ? 0 : `${radiusCqw(item, ratio)}cqw`,
              transform: turn(item),
            }}
            onPointerDown={(event) => startItemGesture(event, item)}
            onDoubleClick={
              editable && item.type === "image" && item.source && !item.locked ? () => onOpenCrop?.(item.id) : undefined
            }
          >
            <ItemBody item={item} asset={assets[assetKey(item)]} ratio={ratio} showPlaceholders={showPlaceholders} />
          </div>
        );
        // The shadow is cast by the turned and tilted item onto the flat board,
        // so it sits on a plain wrapper outside the turn (as in the export).
        return shadow ? (
          <div key={item.id} className="mb-item-shadow" style={{ ...placed, filter: shadow }}>
            {body}
          </div>
        ) : (
          body
        );
      })}

      {editable &&
        selected.map((item) => (
          <div
            key={`selection-${item.id}`}
            className={`mb-selection${item.locked ? " is-locked" : ""}`}
            style={{ ...boxStyle(item), transform: turn(item) }}
          >
            {item.locked && (
              <span className="mb-lock-badge" title="Locked">
                <Lock size={11} strokeWidth={2.2} />
              </span>
            )}
            {single &&
              !item.locked &&
              item.type !== "arrow" &&
              HANDLES.map((handle) => (
                <span
                  key={handle}
                  className={`mb-handle mb-handle-${handle}`}
                  onPointerDown={(event) => startItemGesture(event, item, handle)}
                />
              ))}
            {single && !item.locked && canRotate(item) && (
              <span
                className={`mb-rotate${handleBelow(item) ? " is-below" : ""}`}
                title="Drag to rotate (Shift for 15° steps)"
                onPointerDown={(event) => startRotate(event, item)}
              />
            )}
          </div>
        ))}
      {turning != null && single && (
        <span className="mb-angle-badge" style={{ left: `${single.x + single.w / 2}%`, top: `${single.y + single.h / 2}%` }}>
          {Math.round(turning)}°
        </span>
      )}

      {editable &&
        single?.type === "arrow" &&
        !single.locked &&
        single.points?.length === 2 &&
        pointsOnBoard(single).map(([x, y], index) => (
          <span
            key={`end-${index}`}
            className="mb-handle mb-handle-end"
            style={{ left: `${x}%`, top: `${y}%` }}
            onPointerDown={(event) => startEndpoint(event, single, index)}
          />
        ))}
      {editable &&
        single?.type === "arrow" &&
        single.route === "curved" &&
        !single.locked &&
        single.points?.length === 2 &&
        (() => {
          const [a, b] = pointsOnBoard(single);
          const [mx, my] = curveMidpoint([a[0], a[1] / ratio], [b[0], b[1] / ratio], single.bend ?? DEFAULT_BEND);
          return (
            <span
              className="mb-handle mb-handle-bend"
              title="Drag to bend"
              style={{ left: `${mx}%`, top: `${my * ratio}%` }}
              onPointerDown={(event) => startBend(event, single)}
            />
          );
        })()}

      {groupBounds && <div className="mb-group-bounds" style={boxStyle(groupBounds)} />}
      {marquee && <div className="mb-marquee" style={boxStyle(marquee)} />}
      {editable && tool !== "select" && (
        <div
          className="mb-draw-layer"
          onPointerDown={startDraw}
          onPointerMove={moveDraw}
          onPointerUp={endDraw}
          onPointerCancel={() => setDraft(null)}
        />
      )}
      {draft &&
        (() => {
          const preview = itemFromDraft(draft.points, false);
          return preview ? <DraftPreview item={preview} ratio={ratio} /> : null;
        })()}
      {guides?.x != null && <div className="mb-guide mb-guide-x" style={{ left: `${guides.x}%` }} />}
      {guides?.y != null && <div className="mb-guide mb-guide-y" style={{ top: `${guides.y}%` }} />}
    </div>
  );

  if (!fit) return boardNode;
  return (
    <div className="mb-stage" ref={stageRef}>
      {fitWidth != null && boardNode}
    </div>
  );
}
